import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridBaseQuery } from '@core';
import { CaseFormEntity } from '../../domain/entities/case-form.entity';
import { CaseFormApi } from '../api/case-form.api';
import { AddFieldRequestDto } from '../dtos/add-field-request.dto';
import { FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';
import { FieldDefinitionLibraryResponseDto } from '../dtos/field-definition-library-response.dto';
import { FormVersionDetailRepositoryResponseDto } from '../dtos/form-version-detail-repository-response.dto';
import { FormVersionsListResponseDto } from '../dtos/form-versions-list-response.dto';
import { ReorderFieldsRequestDto } from '../dtos/reorder-fields-request.dto';
import { UpdateFieldRequestDto } from '../dtos/update-field-request.dto';
import { CreateCaseFormRequestDto } from '../dtos/create-case-form-request.dto';
import { SensitivityCategoryEntity } from '../../domain/entities/sensitivity-category.entity';
import {
  CaseFormMapper,
  UpdateCaseFormParams,
} from '../mappers/case-form.mapper';

/**
 * Case Form Repository
 * Handles data operations for case forms: API calls and DTO mapping
 * NO STATE MANAGEMENT - state is handled by use cases
 */
@Injectable({ providedIn: 'root' })
export class CaseFormRepository {
  private readonly _api = inject(CaseFormApi);
  private readonly _mapper = inject(CaseFormMapper);

  /**
   * Retrieves all case forms
   */
  public getAllCaseForms(): Observable<CaseFormEntity[]> {
    return this._api.getAllCaseForms().pipe(map((dtos) => this._mapper.toDomainArray(dtos)));
  }

  /**
   * Retrieves case forms with pagination
   */
  public getCaseFormsWithPaging(query: GridBaseQuery): Observable<{ data: CaseFormEntity[]; totalElements: number }> {
    return this._api.getCaseFormsWithPaging(query).pipe(
      map((result) => ({
        data: (result.data || []).map((dto) => this._mapper.toDomain(dto)),
        totalElements: result.totalElements || 0,
      })),
    );
  }

  /**
   * Retrieves case forms filtered by case type ID
   */
  public getCaseFormsByCaseTypeId(caseTypeId: string): Observable<CaseFormEntity[]> {
    return this._api.getCaseFormsByCaseTypeId(caseTypeId).pipe(
      map((dtos) => this._mapper.toDomainArray(dtos))
    );
  }

  /**
   * Retrieves a single case form by case type ID
   * Returns undefined if no form is found for the case type
   */
  public getCaseFormByCaseTypeId(caseTypeId: string): Observable<CaseFormEntity | undefined> {
    return this._api.getCaseFormByCaseTypeId(caseTypeId).pipe(
      map((dto) => dto ? this._mapper.toDomain(dto) : undefined)
    );
  }

  /**
   * Retrieves a case form by ID (basic info only)
   */
  public getCaseFormById(id: string): Observable<CaseFormEntity | undefined> {
    return this._api.getCaseFormById(id).pipe(
      map((dto) => dto ? this._mapper.toDomain(dto) : undefined)
    );
  }

  /**
   * Retrieves all versions for a case form
   */
  public getCaseFormVersionsByFormId(formId: string): Observable<FormVersionsListResponseDto> {
    return this._api.getCaseFormVersionsByFormId(formId);
  }

  /**
   * Retrieves version details by version ID
   */
  public getCaseFormVersionDetailByVersionId(versionId: string): Observable<FormVersionDetailRepositoryResponseDto> {
    return this._api.getCaseFormVersionDetailByVersionId(versionId).pipe(
      map((dto) => this._mapper.mapFormVersionDetail(dto))
    );
  }

  /**
   * Creates a new draft version for a case form
   */
  public createCaseFormDraftVersion(formId: string): Observable<{ versionId: string }> {
    return this._api.createCaseFormDraftVersion(formId);
  }

  /**
   * Publishes the draft version of a case form
   */
  public publishDraft(formId: string): Observable<{ versionId: string }> {
    return this._api.publishDraft(formId);
  }

  /**
   * Discards the draft version of a case form
   */
  public discardDraft(formId: string): Observable<void> {
    return this._api.discardDraft(formId);
  }

  /**
   * Updates an existing case form
   */
  public updateCaseForm(id: string, params: UpdateCaseFormParams): Observable<void> {
    const dto = this._mapper.toUpdateDto(params);
    return this._api.updateCaseForm(id, dto);
  }

  /**
   * Deletes a case form
   */
  public deleteCaseForm(id: string): Observable<void> {
    return this._api.deleteCaseForm(id);
  }

  /**
   * Adds a field to a form version
   */
  public addField(versionId: string, dto: AddFieldRequestDto): Observable<{ id: string }> {
    return this._api.addField(versionId, dto);
  }

  /**
   * Updates a field in a form version
   */
  public updateField(versionId: string, fieldId: string, dto: UpdateFieldRequestDto): Observable<{ id: string }> {
    return this._api.updateField(versionId, fieldId, dto);
  }

  /**
   * Removes a field from a form version
   */
  public removeField(versionId: string, fieldId: string): Observable<void> {
    return this._api.removeField(versionId, fieldId);
  }

  /**
   * Reorders fields in a form version
   */
  public reorderFields(versionId: string, dto: ReorderFieldsRequestDto): Observable<void> {
    return this._api.reorderFields(versionId, dto);
  }

  /**
   * Gets field definitions library with constraint types
   */
  public getFieldDefinitions(): Observable<FieldDefinitionLibraryResponseDto> {
    return this._api.getFieldDefinitions();
  }

  /**
   * Gets field types with operators and constraints
   * Returns complete field type information including operators for visibility rules
   * To get flattened constraints, use CaseFormMapper.flattenFieldTypeConstraints()
   */
  public getFieldTypes(): Observable<FieldTypeResponseDto[]> {
    return this._api.getFieldTypes();
  }

  /**
   * Creates a new case form
   */
  public createCaseForm(params: {
    name: string;
    description?: string;
    caseTypeId?: string;
  }): Observable<{ formId: string; versionId: string }> {
    const dto: CreateCaseFormRequestDto = { ...params };
    return this._api.createCaseForm(dto);
  }

  /**
   * Retrieves the sensitivity categories that a form field can be classified with.
   * HCMI-889: values come from the backend seed and drive the audit-log filter.
   */
  public getSensitivityCategories(): Observable<SensitivityCategoryEntity[]> {
    return this._api.getSensitivityCategories().pipe(
      map((dtos) => dtos.map((dto) => Object.assign(new SensitivityCategoryEntity(), dto))),
    );
  }
}
